/**
 * 🛡️ CPF INDEX SERVICE — ÍNDICE ÚNICO DE CPF PARA PRATICANTES ATIVOS
 *
 * @module CPFIndexService
 * @description Garante unicidade de CPF entre praticantes ATIVOS
 *              (metadados.successorId === "") via documento com ID = CPF (apenas dígitos).
 *
 * Coleção: `praticantes.cpf.index`
 *   - Doc ID = CPF limpo (11 dígitos)
 *   - Body   = { praticanteId: <ID estável do praticante>, createdAt, ownerid, cpfFormatado }
 *
 * Como o ID do praticante (documento atual) NUNCA muda, a manutenção do índice
 * é simples: só é tocado em criação, edição que altere o CPF, ou desativação.
 *
 * Cópias históricas (criadas via addDoc em update/versionamento) NÃO tocam o índice,
 * pois continuam se referindo ao mesmo CPF do mesmo praticante.
 *
 * 🔒 O índice é a fonte de verdade para impedir CPFs duplicados entre versões atuais.
 *    A transação falha se o documento de índice já existir, garantindo atomicidade.
 */

import { db } from "../utils/firestore";
import {
  doc,
  getDoc,
  deleteDoc,
  Timestamp,
  runTransaction,
} from "firebase/firestore";

export const CPF_INDEX_COLLECTION = "praticantes.cpf.index";

/**
 * Extrai apenas dígitos do CPF.
 * @param {string} cpf
 * @returns {string} CPF com 11 dígitos ou "" se inválido.
 */
export const getCpfDigits = (cpf) => {
  if (!cpf) return "";
  const d = String(cpf).replace(/\D/g, "");
  return d.length === 11 ? d : "";
};

/**
 * Formata CPF (XXX.XXX.XXX-XX) a partir dos dígitos.
 */
export const formatCpf = (cpfDigits) => {
  if (!cpfDigits || cpfDigits.length !== 11) return "";
  return cpfDigits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

class CPFIndexService {
  /**
   * Lê o documento de índice para um CPF.
   * @param {string} cpf - CPF em qualquer formato
   * @returns {Promise<{id:string, praticanteId:string}|null>}
   */
  getByCPF = async (cpf) => {
    const cpfDigits = getCpfDigits(cpf);
    if (!cpfDigits) return null;
    const snap = await getDoc(doc(db, CPF_INDEX_COLLECTION, cpfDigits));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  };

  /**
   * Reserva atomicamente o CPF para um praticante.
   * FALHA se outro praticante já tiver reservado este CPF.
   *
   * ⚠️ Use DENTRO de uma transaction já aberta quando precisar atomicidade
   *     conjunta com a criação do praticante. Este método é para uso standalone.
   *
   * @param {string} cpf
   * @param {string} praticanteId - ID estável do praticante
   * @param {string} ownerid - UID do usuário que está criando
   * @throws {Error} se CPF já reservado por OUTRO praticante
   */
  reserve = async (cpf, praticanteId, ownerid) => {
    const cpfDigits = getCpfDigits(cpf);
    if (!cpfDigits) throw new Error("CPF inválido para reserva no índice.");
    if (!praticanteId) throw new Error("praticanteId obrigatório para reserva.");

    await runTransaction(db, async (transaction) => {
      const ref = doc(db, CPF_INDEX_COLLECTION, cpfDigits);
      const snap = await transaction.get(ref);
      if (snap.exists()) {
        const existing = snap.data();
        if (existing.praticanteId && existing.praticanteId !== praticanteId) {
          throw new Error(
            `CPF já cadastrado no sistema (índice aponta para praticante ${existing.praticanteId}).`
          );
        }
        // Mesmo praticante → idempotente, não faz nada
        return;
      }
      transaction.set(ref, {
        praticanteId,
        cpfFormatado: formatCpf(cpfDigits),
        ownerid: ownerid || null,
        createdAt: Timestamp.fromDate(new Date()),
      });
    });
  };

  /**
   * Libera a reserva de um CPF (usado em edição que muda o CPF).
   * Só libera se o praticanteId bater com o registrado (segurança).
   *
   * @param {string} cpf
   * @param {string} praticanteId - ID estável esperado no índice
   */
  release = async (cpf, praticanteId) => {
    const cpfDigits = getCpfDigits(cpf);
    if (!cpfDigits) return;
    const ref = doc(db, CPF_INDEX_COLLECTION, cpfDigits);
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(ref);
      if (!snap.exists()) return;
      const data = snap.data();
      if (data.praticanteId && data.praticanteId !== praticanteId) {
        console.warn(
          `[CPFIndexService.release] Ignorando liberação: índice ${cpfDigits} pertence a ${data.praticanteId}, não a ${praticanteId}.`
        );
        return;
      }
      transaction.delete(ref);
    });
  };

  /**
   * Migração atômica de CPF (edição que altera o CPF do praticante).
   * Libera o antigo e reserva o novo em uma única transação.
   *
   * @param {string} oldCpf
   * @param {string} newCpf
   * @param {string} praticanteId
   * @param {string} ownerid
   */
  migrate = async (oldCpf, newCpf, praticanteId, ownerid) => {
    const oldDigits = getCpfDigits(oldCpf);
    const newDigits = getCpfDigits(newCpf);
    if (oldDigits === newDigits) return; // nada a fazer

    await runTransaction(db, async (transaction) => {
      // 1. Verifica se novo CPF já está reservado por outro praticante
      if (newDigits) {
        const newRef = doc(db, CPF_INDEX_COLLECTION, newDigits);
        const newSnap = await transaction.get(newRef);
        if (newSnap.exists()) {
          const existing = newSnap.data();
          if (existing.praticanteId && existing.praticanteId !== praticanteId) {
            throw new Error(
              `Novo CPF já cadastrado no sistema (índice aponta para praticante ${existing.praticanteId}).`
            );
          }
        }
      }

      // 2. Lê índice antigo (se existir e pertencer ao praticante)
      if (oldDigits) {
        const oldRef = doc(db, CPF_INDEX_COLLECTION, oldDigits);
        const oldSnap = await transaction.get(oldRef);
        if (oldSnap.exists()) {
          const data = oldSnap.data();
          if (!data.praticanteId || data.praticanteId === praticanteId) {
            transaction.delete(oldRef);
          }
        }
      }

      // 3. Cria novo índice
      if (newDigits) {
        const newRef = doc(db, CPF_INDEX_COLLECTION, newDigits);
        transaction.set(newRef, {
          praticanteId,
          cpfFormatado: formatCpf(newDigits),
          ownerid: ownerid || null,
          createdAt: Timestamp.fromDate(new Date()),
        });
      }
    });
  };

  /**
   * Remoção direta (uso administrativo/limpeza). Não recomendado em fluxo normal.
   */
  forceDelete = async (cpf) => {
    const cpfDigits = getCpfDigits(cpf);
    if (!cpfDigits) return;
    await deleteDoc(doc(db, CPF_INDEX_COLLECTION, cpfDigits));
  };
}

export default new CPFIndexService();
